import React, { useCallback, useRef } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import { ExternalActivityListener, Icon, RelativePortal, Tooltip, TooltipPosition, TriggerableMenu } from 'venn-ui-kit';
import { convertToLetters } from '../../../components/utils';
import { cn } from '@/lib/utils';
import FundTypeahead from '../../../../../fund-typeahead/FundTypeahead';
import {
  SCOPE_OPTION_ALL_FUNDS,
  SCOPE_OPTION_USER_UPLOADED_FUNDS,
  SCOPE_TYPES,
  type Option as FundTypeaheadOption,
} from '../../../../../fund-typeahead/types';
import { type ReviewCellRendererProps } from '../helpers';
import { DataUploaderMode } from '../../../types';
import { performMappingUpdate } from './rendererHelpers';
import type { ColumnMapping } from 'venn-api';

enum InvestmentActionType {
  RenameOrMap,
  Discard,
  Restore,
}

const renameOption = {
  value: InvestmentActionType.RenameOrMap,
  children: <span>Rename or map to existing investment</span>,
};
const discardOption = {
  value: InvestmentActionType.Discard,
  children: <span>Discard investment</span>,
};
const restoreOption = {
  value: InvestmentActionType.Restore,
  children: <span>Restore investment</span>,
};

export const NameCellRenderer = (props: ICellRendererParams<ColumnMapping> & ReviewCellRendererProps) => {
  // Using ref to trigger the menu on name click
  const menuRef = useRef<HTMLSpanElement>(null);
  const {
    additionalMetadata,
    existingOwnerships,
    mode,
    onMappingChange,
    setExistingOwnerships,
    currentMapping,
    value,
  } = props;
  const data = props.data!;
  const { origin, seriesId } = data;
  const { deletedTypeId, fundTypeId } = additionalMetadata;
  const { isDeleted, isDisabled, isNew } = additionalMetadata.perSeries[seriesId]!;
  const [showRenameOrMap, setShowRenameOrMap] = React.useState(false);

  const initialScope =
    mode === DataUploaderMode.Returns || mode === DataUploaderMode.Privates
      ? SCOPE_TYPES.USER_UPLOADED_FUNDS
      : SCOPE_TYPES.ALL_FUNDS;
  const scopeOptions =
    mode === DataUploaderMode.Returns || mode === DataUploaderMode.Privates
      ? SCOPE_OPTION_USER_UPLOADED_FUNDS
      : SCOPE_OPTION_ALL_FUNDS;

  const tooltipContent = (
    <div className="text-[12px]">
      <div className="font-bold">{value}</div>
      <div>
        Starting cell in upload: {convertToLetters(origin.colIndex)}
        {origin.rowIndex + 1}
      </div>
    </div>
  );

  const onFundSelect = useCallback(
    (option: FundTypeaheadOption) => {
      const newOwnerships = (option.fund?.ownerContextIds ?? []) as string[];
      const updatedExistingOwnerships = {
        ...existingOwnerships,
        [seriesId]: newOwnerships,
      };
      const updatedColumns = currentMapping.columns.map((mapping) => {
        if (mapping.seriesId === seriesId) {
          const newFundSettings = {
            fundId: option.fund?.id ?? '',
            fundName: option.fund?.name ?? option.label,
            ownerContextIds: newOwnerships,
          };
          return { ...mapping, ...newFundSettings };
        }
        return mapping;
      });
      const updatedMapping = { ...currentMapping, columns: updatedColumns };

      setExistingOwnerships(updatedExistingOwnerships);
      onMappingChange(updatedMapping);
      setShowRenameOrMap(false);
    },
    [setExistingOwnerships, onMappingChange, setShowRenameOrMap, currentMapping, existingOwnerships, seriesId],
  );

  return (
    <div className="flex gap-[2px] max-w-full min-w-0 cursor-pointer relative">
      <Tooltip
        usePortal
        position={TooltipPosition.Top}
        content={tooltipContent}
        className="whitespace-nowrap min-w-0 overflow-hidden overflow-ellipsis"
      >
        <button
          type="button"
          className={cn([
            'font-bold text-[14px] text-venn-black whitespace-nowrap min-w-0 overflow-hidden overflow-ellipsis max-w-full hover:text-venn-black',
            isDisabled ? 'text-venn-grey-60' : null,
          ])}
          onClick={() => menuRef.current?.click()}
        >
          {value}
        </button>
      </Tooltip>
      <div>
        <TriggerableMenu
          usePortal
          ref={menuRef}
          innerClassName="mt-[19px]"
          options={[renameOption, isDeleted ? restoreOption : discardOption]}
          onSelect={(selectedOption) => {
            if (selectedOption.value === InvestmentActionType.RenameOrMap) {
              setShowRenameOrMap(true);
            }
            if (
              selectedOption.value === InvestmentActionType.Discard ||
              selectedOption.value === InvestmentActionType.Restore
            ) {
              const values = [data.appendId, data.currency, isDeleted ? fundTypeId : deletedTypeId];
              performMappingUpdate(props, values);
            }
          }}
        >
          <Icon type="ellipsis-h" className="text-[14px] text-venn-dark-blue" />
        </TriggerableMenu>
      </div>
      {showRenameOrMap && (
        <div className="absolute top-0 z-[10000]">
          <ExternalActivityListener
            debounce={false}
            onExternalActivity={() => setShowRenameOrMap(false)}
            listeningEnabled={showRenameOrMap}
          >
            <RelativePortal>
              <FundTypeahead
                className="w-[350px] p-0 [&_button]:rounded-none [&_button:after]:hidden [&_section]:border [&_section]:border-solid [&_section]:border-venn-primary-dark [&_section+div]:mt-[2px] [&_input]:rounded-none"
                isNew={isNew}
                value={value}
                closeOnSelect
                onFundSelect={onFundSelect}
                onNewSelect={onFundSelect}
                initialScope={initialScope}
                autoFocus
                hideButton
                scopeOptions={[scopeOptions]}
              />
            </RelativePortal>
          </ExternalActivityListener>
        </div>
      )}
    </div>
  );
};
